import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { MapChart } from "@amcharts/amcharts5/.internal/charts/map/MapChart";
import { MapPolygonSeries } from "@amcharts/amcharts5/.internal/charts/map/MapPolygonSeries";
import { MapPointSeries } from "@amcharts/amcharts5/.internal/charts/map/MapPointSeries";
import { geoMercator, getGeoCentroid } from "@amcharts/amcharts5/map";
import indiaGeodata from "@amcharts/amcharts5-geodata/india2023High";
import { callAPI } from "../../../../../axios";
import StreetMapView from "./StreetMapView";
import {
  assignHotelsToStates,
  averagePoint,
  buildRegionalTree,
  getStateFeature,
  hotelStats,
  matchesMapHotelFilter,
  stateMapTooltipText,
  uniqueChainIds,
} from "./geoUtils";
import "./IndiaMapWithSTB.css";

const HEAT_MIN = 0xadd8e6;
const HEAT_MAX = 0x5165af;
const EMPTY_REGION = { level: "india", stateId: null, stateName: null, cityKey: null, cityName: null };
const EMPTY_STREET_HOTELS = [];

const formatCount = (count) => {
  const num = Number(count ?? 0);
  if (num >= 1000) {
    const formatted = (num / 1000).toFixed(1);
    return formatted.endsWith(".0") ? `${parseInt(formatted, 10)}K` : `${formatted}K`;
  }
  return String(num);
};

const featureStateId = (feature) => feature?.id ?? feature?.properties?.id;

const IndiaMapWithSTB = ({
  filterType = "all",
  brandFilter = "",
  brandMatchAliases = [],
  onRegionChange,
}) => {
  const chartDivRef = useRef(null);
  const onRegionChangeRef = useRef(onRegionChange);
  const goStateRef = useRef(() => {});
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState(EMPTY_REGION);

  onRegionChangeRef.current = onRegionChange;

  useEffect(() => {
    let cancelled = false;
    const fetchHotels = async () => {
      try {
        const response = await callAPI("/indiaMap");
        if (!cancelled) {
          setHotels(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        console.error("Error fetching hotel map data:", error);
        if (!cancelled) {
          setHotels([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchHotels();
    return () => {
      cancelled = true;
    };
  }, []);

  const assignedHotels = useMemo(() => {
    const filteredHotels = hotels.filter((hotel) =>
      matchesMapHotelFilter(hotel, filterType, brandFilter, brandMatchAliases)
    );
    return assignHotelsToStates(filteredHotels, indiaGeodata);
  }, [hotels, filterType, brandFilter, brandMatchAliases]);
  const regionalTree = useMemo(() => buildRegionalTree(assignedHotels), [assignedHotels]);
  const hasMappedHotels = assignedHotels.some((hotel) => hotel.stateId);
  const showStreetMap = region.level !== "india";
  const selectedState = region.stateId ? regionalTree[region.stateId] : null;
  const selectedCity = selectedState?.cities?.[region.cityKey];
  const stateFeature = region.stateId ? getStateFeature(indiaGeodata, region.stateId) : null;
  const stateCities = useMemo(
    () => (selectedState ? Object.values(selectedState.cities) : []),
    [selectedState]
  );
  const streetHotels = selectedCity?.hotels ?? EMPTY_STREET_HOTELS;

  const emitRegion = useCallback((nextRegion, hotelList) => {
    setRegion(nextRegion);
    onRegionChangeRef.current?.({
      level: nextRegion.level,
      stateName: nextRegion.stateName,
      cityName: nextRegion.cityName,
      chainIds: nextRegion.level === "india" ? null : uniqueChainIds(hotelList),
    });
  }, []);

  const goIndia = useCallback(() => {
    emitRegion(EMPTY_REGION, assignedHotels);
  }, [assignedHotels, emitRegion]);

  const goState = useCallback(
    (stateId) => {
      const state = regionalTree[stateId];
      if (!state) {
        return;
      }
      emitRegion(
        { level: "state", stateId, stateName: state.name, cityKey: null, cityName: null },
        state.hotels
      );
    },
    [emitRegion, regionalTree]
  );

  const goCity = useCallback(
    (cityKey) => {
      const state = regionalTree[region.stateId];
      const city = state?.cities?.[cityKey];
      if (!city) {
        return;
      }
      emitRegion(
        {
          level: "city",
          stateId: state.id,
          stateName: state.name,
          cityKey,
          cityName: city.name,
        },
        city.hotels
      );
    },
    [emitRegion, region.stateId, regionalTree]
  );

  goStateRef.current = goState;

  const skipFilterReset = useRef(true);
  useEffect(() => {
    if (skipFilterReset.current) {
      skipFilterReset.current = false;
      return;
    }
    goIndia();
  }, [filterType, brandFilter, brandMatchAliases]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && !hasMappedHotels) {
      goIndia();
    }
  }, [loading, hasMappedHotels]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!chartDivRef.current || loading || !hasMappedHotels || showStreetMap) {
      return undefined;
    }

    am5.array.each(am5.registry.rootElements, (rootItem) => {
      if (rootItem.dom === chartDivRef.current) {
        rootItem.dispose();
      }
    });

    const root = am5.Root.new(chartDivRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      MapChart.new(root, {
        panX: "translateX",
        panY: "translateY",
        projection: geoMercator(),
        layout: root.verticalLayout,
        wheelY: "zoom",
        pinchZoom: true,
        maxZoomLevel: 16,
      })
    );

    const polygonSeries = chart.series.push(
      MapPolygonSeries.new(root, {
        geoJSON: indiaGeodata,
        valueField: "value",
        calculateAggregates: true,
      })
    );

    const createStandardTooltip = () => {
      const tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        autoTextColor: false,
      });
      tooltip.get("background").setAll({
        fill: am5.color(0x303436),
        fillOpacity: 0.95,
        stroke: am5.color(0x303436),
        strokeWidth: 0,
        cornerRadius: 6,
        shadowColor: am5.color(0x000000),
        shadowBlur: 10,
        shadowOffsetY: 4,
        shadowOpacity: 0.35,
      });
      tooltip.label.setAll({
        fill: am5.color(0xffffff),
        fontSize: 12,
        lineHeight: 1.5,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 12,
        paddingRight: 12,
        populateText: true,
      });
      return tooltip;
    };

    const mapTooltip = createStandardTooltip();
    polygonSeries.set("tooltip", mapTooltip);

    const allFeatures = indiaGeodata.features || [];
    const stateTooltipById = {};
    const polygonData = allFeatures.map((feature) => {
      const stateId = featureStateId(feature);
      const state = regionalTree[stateId];
      const stateHotels = state?.hotels ?? [];
      const stats = hotelStats(stateHotels);
      const stateName = feature.properties?.name || state?.name || stateId;
      const tooltipText = stateMapTooltipText(stateName, stateHotels);
      stateTooltipById[stateId] = tooltipText;
      return {
        id: stateId,
        name: stateName,
        value: stats.hotels > 0 ? stats.hotels : undefined,
        hotels: stats.hotels,
        hms: stats.hms,
        thirdParty: stats.thirdParty,
        stbCount: stats.stbCount,
        tooltipText,
      };
    });
    const maxHotels = Math.max(
      1,
      ...polygonData.map((item) => (typeof item.value === "number" ? item.value : 0))
    );

    polygonSeries.mapPolygons.template.setAll({
      tooltipText: "{tooltipText}",
      interactive: true,
      cursorOverStyle: "pointer",
      fill: am5.color(0xb8b2b2),
      stroke: am5.color(0x000000),
      strokeWidth: 0.7,
    });
    polygonSeries.mapPolygons.template.states.create("hover", {
      fill: am5.color(0x49cc90),
    });

    polygonSeries.set("heatRules", [
      {
        target: polygonSeries.mapPolygons.template,
        dataField: "value",
        min: am5.color(HEAT_MIN),
        max: am5.color(HEAT_MAX),
        neutral: am5.color(0xb8b2b2),
        minValue: 1,
        maxValue: Math.max(2, maxHotels),
        key: "fill",
      },
    ]);

    polygonSeries.data.setAll(polygonData);

    const navigateToStateView = (stateId) => {
      if (stateId == null || stateId === "") {
        return;
      }
      const resolvedId =
        regionalTree[stateId] != null
          ? stateId
          : Object.keys(regionalTree).find((key) => String(key) === String(stateId));
      if (resolvedId != null) {
        goStateRef.current(resolvedId);
      }
    };

    const stateIdFromSprite = (sprite) => {
      let current = sprite;
      while (current) {
        const ctx = current.dataItem?.dataContext;
        const id = ctx?.id ?? ctx?.stateId ?? current.dataItem?.get("id");
        if (id != null && id !== "") {
          return id;
        }
        current = current.parent;
      }
      return null;
    };

    polygonSeries.events.on("datavalidated", () => {
      polygonSeries.mapPolygons.each((mapPolygon) => {
        if (mapPolygon.getPrivate("stateNavClickBound")) {
          return;
        }
        mapPolygon.setPrivate("stateNavClickBound", true);
        mapPolygon.events.on("click", (event) => {
          navigateToStateView(stateIdFromSprite(mapPolygon));
        });
      });
    });

    const pointSeries = chart.series.push(MapPointSeries.new(root, {}));
    pointSeries.set("tooltip", mapTooltip);
    pointSeries.bullets.push(() => {
      const container = am5.Container.new(root, {
        tooltipText: "{tooltipText}",
        interactive: true,
        cursorOverStyle: "pointer",
      });

      const circle = container.children.push(
        am5.Circle.new(root, {
          radius: 13,
          fill: am5.color(0xe10092),
          stroke: am5.color(0xffffff),
          strokeWidth: 2,
        })
      );

      const label = container.children.push(
        am5.Label.new(root, {
          text: "{displayCount}",
          fill: am5.color(0xffffff),
          populateText: true,
          centerX: am5.p50,
          centerY: am5.p50,
          fontWeight: "700",
          fontSize: 11,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        })
      );

      const openStateFromBullet = (event) => {
        navigateToStateView(stateIdFromSprite(event.target));
      };

      container.events.on("click", openStateFromBullet);
      circle.events.on("click", openStateFromBullet);
      label.events.on("click", openStateFromBullet);

      return am5.Bullet.new(root, { sprite: container });
    });
    pointSeries.data.setAll(
      Object.values(regionalTree).map((state) => {
        const stats = hotelStats(state.hotels);
        const feature = getStateFeature(indiaGeodata, state.id);
        const centroid = feature?.geometry ? getGeoCentroid(feature.geometry) : null;
        const coordinates =
          centroid && Number.isFinite(centroid.longitude) && Number.isFinite(centroid.latitude)
            ? [centroid.longitude, centroid.latitude]
            : (() => {
                const fallback = averagePoint(state.hotels);
                return [fallback.lng, fallback.lat];
              })();

        const countVal = stats.hotels;
        const displayCount = formatCount(countVal);

        return {
          polygonId: state.id,
          stateId: state.id,
          name: state.name,
          displayCount,
          stores: displayCount,
          hotelCount: stats.hotels,
          stbCount: stats.stbCount,
          tooltipText: stateTooltipById[state.id] ?? stateMapTooltipText(state.name, state.hotels),
          geometry: { type: "Point", coordinates },
        };
      })
    );

    return () => {
      root.dispose();
    };
  }, [assignedHotels, hasMappedHotels, loading, regionalTree, showStreetMap]);

  return (
    <div className="stb-map-container">
      <div className="india-map-toolbar">
        <div className="india-map-breadcrumb">
          {region.level === "india" ? (
            <span className="current">India</span>
          ) : (
            <button type="button" onClick={goIndia}>
              India
            </button>
          )}
          {region.stateName && (
            <>
              <span>&gt;</span>
              {region.level === "state" ? (
                <span className="current">{region.stateName}</span>
              ) : (
                <button type="button" onClick={() => goState(region.stateId)}>
                  {region.stateName}
                </button>
              )}
            </>
          )}
          {region.cityName && (
            <>
              <span>&gt;</span>
              <span className="current">{region.cityName}</span>
            </>
          )}
        </div>
        <div className="india-map-legend">
          {showStreetMap ? (
            "Street map · cities and roads"
          ) : (
            <>
              <span /> Fewer → more hotels
            </>
          )}
        </div>
      </div>
      {loading && <div className="india-map-loading">Loading map…</div>}
      {!loading && !hasMappedHotels && (
        <div className="india-map-empty">No hotels with map coordinates for this filter.</div>
      )}
      {showStreetMap && selectedState ? (
        <StreetMapView
          level={region.level}
          stateFeature={stateFeature}
          cities={stateCities}
          hotels={streetHotels}
          onCityClick={goCity}
        />
      ) : (
        <div ref={chartDivRef} className="india-map-chart" />
      )}
    </div>
  );
};

export default IndiaMapWithSTB;
